import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useChatbot } from '@/context/ChatbotContext';
import { useModuleNavigation } from '@/hooks/useSSO';
import {
  DollarSign,
  Users,
  FileText,
  TrendingUp,
  LogOut,
  ArrowRight,
  LayoutGrid,
  Settings,
  UserCog
} from 'lucide-react';

// Icon mapping
const iconMap = {
  DollarSign: DollarSign,
  Users: Users,
  FileText: FileText,
  TrendingUp: TrendingUp,
  Settings: Settings,
  UserCog: UserCog
};

// Color mapping
const colorMap = {
  green: {
    bg: 'bg-green-500',
    light: 'bg-green-50',
    text: 'text-green-600',
    border: 'border-green-200',
    hover: 'hover:border-green-400 hover:bg-green-50'
  },
  blue: {
    bg: 'bg-blue-500',
    light: 'bg-blue-50',
    text: 'text-blue-600',
    border: 'border-blue-200',
    hover: 'hover:border-blue-400 hover:bg-blue-50'
  },
  purple: {
    bg: 'bg-purple-500',
    light: 'bg-purple-50',
    text: 'text-purple-600',
    border: 'border-purple-200',
    hover: 'hover:border-purple-400 hover:bg-purple-50'
  },
  orange: {
    bg: 'bg-orange-500',
    light: 'bg-orange-50',
    text: 'text-orange-600',
    border: 'border-orange-200',
    hover: 'hover:border-orange-400 hover:bg-orange-50'
  },
  slate: {
    bg: 'bg-slate-700',
    light: 'bg-slate-100',
    text: 'text-slate-700',
    border: 'border-slate-300',
    hover: 'hover:border-slate-500 hover:bg-slate-100'
  },
  red: {
    bg: 'bg-red-500',
    light: 'bg-red-50',
    text: 'text-red-600',
    border: 'border-red-200',
    hover: 'hover:border-red-400 hover:bg-red-50'
  }
};

// User Management (Super Admin only)
const USER_MANAGEMENT = {
  id: 'user_management',
  name: 'User Management',
  description: 'Create users and manage product access',
  url: '/super-admin/users',
  icon: 'UserCog',
  color: 'red',
  isInternal: true
};

export default function PortalPage() {
  const navigate = useNavigate();
  const { navigateToModule } = useModuleNavigation();
  const [user, setUser] = useState(null);
  const { openChatbot } = useChatbot();

  useEffect(() => {
    // Check if user is logged in
    const userData = localStorage.getItem('userData');
    const token = localStorage.getItem('authToken');
    
    if (!userData || !token) {
      navigate('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    
    // Sync tokens for all modules on portal load
    localStorage.setItem('financeToken', token);
    localStorage.setItem('hrToken', token);
    localStorage.setItem('salesToken', token);
    localStorage.setItem('docsToken', token);

    // If user has only one product and not super admin, redirect directly
    if (parsedUser.role !== 'super_admin' && parsedUser.products?.length === 1) {
      const product = parsedUser.products[0];
      // All products now use the internal suite routes
      navigate(product.url);
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    navigate('/login');
  };

  const openProduct = (product) => {
    // All products are now internal suite routes
    navigate(product.url);
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const isAdmin = user.role === 'super_admin';
  
  // Build product list - add User Management for super admin
  const allProducts = isAdmin 
    ? [USER_MANAGEMENT, ...(user.products || [])]
    : (user.products || []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-xl border-b border-slate-800/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_aurous-ecosystem/artifacts/x28qd6xo_30882183-9DD0-40B4-98FF-3BB81A460020.png" 
                alt="Aurous" 
                className="h-8 w-auto"
              />
            </Link>

            {/* User info & actions */}
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-white">{user.name}</p>
                <p className="text-xs text-slate-400">{user.email}</p>
              </div>
              
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section - Compact */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30 mb-3">
            <LayoutGrid className="w-8 h-8 text-blue-400" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
            Welcome, {user.name.split(' ')[0]}!
          </h1>
          <p className="text-sm text-slate-400 mb-2">
            Select a product to get started
          </p>
          <Badge className="bg-gradient-to-r from-blue-600 to-purple-600 text-white border-0 text-xs px-3 py-1 capitalize">
            {user.role.replace(/_/g, ' ')}
          </Badge>
        </div>

        {/* Products Grid - 3x2 for 6 items in single view */}
        {allProducts.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {allProducts.map((product) => {
              const Icon = iconMap[product.icon] || FileText;
              const colors = colorMap[product.color] || colorMap.blue;
              
              return (
                <div 
                  key={product.id}
                  className="group relative bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-5 cursor-pointer transition-all duration-300 hover:bg-slate-800 hover:border-slate-600 hover:shadow-2xl hover:shadow-blue-500/10 hover:-translate-y-1"
                  onClick={() => openProduct(product)}
                >
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  <div className="relative text-center">
                    <div className={`w-14 h-14 rounded-xl ${colors.light} flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform`}>
                      <Icon className={`w-7 h-7 ${colors.text}`} />
                    </div>
                    <h3 className="text-base font-semibold text-white mb-1">{product.name}</h3>
                    <p className="text-xs text-slate-400 mb-3 line-clamp-2">{product.description}</p>
                    <Button size="sm" className={`w-full ${colors.bg} hover:opacity-90 text-white text-xs py-2`}>
                      Open 
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-2xl bg-slate-800 flex items-center justify-center mx-auto mb-4">
              <LayoutGrid className="w-8 h-8 text-slate-500" />
            </div>
            <h2 className="text-lg font-bold text-slate-300 mb-2">No Products Assigned</h2>
            <p className="text-sm text-slate-500 mb-4">
              You don&apos;t have access to any products yet. Please contact your administrator.
            </p>
            <Button variant="outline" size="sm" onClick={() => openChatbot('chat')} className="border-slate-600 text-slate-300 hover:bg-slate-700">Contact Support</Button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-auto py-4 text-center text-xs text-slate-500">
        <p>© 2025 Aurous Consultancy. All rights reserved.</p>
      </footer>
    </div>
  );
}
